import MonacoEditor, { BeforeMount, Monaco, OnMount } from "@monaco-editor/react";
import { editor } from "monaco-editor";
import { useEffect, useRef } from "react";
import math from "../Mathjs/CustomMathjsInstance";
import { Constant, constants } from "./constants.ts";
import { jsFunctions } from "./jsFunctions.ts";
import { keywords } from "./keywords.ts";

type StringRange = {
  start: number;
  end: number;
};

export const CodeEditor = ({
  fields,
  globalParameters,
  internalExpression,
  setInternalExpression,
  setEditor,
}) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const disposablesRef = useRef<any[]>([]);

  useEffect(() => {
    return () => {
      disposablesRef.current.forEach((disposable) => {
        disposable.dispose();
      });
    };
  }, []);

  useEffect(() => {
    validateExpression();
  }, [internalExpression]);

  const handleEditorWillMount: BeforeMount = (monaco) => {
    const registeredLanguages = monaco.languages.getLanguages();
    const isAtlasRegistered = registeredLanguages.some((lang) => lang.id === "Atlas");

    if (!isAtlasRegistered) {
      languageSetup(monaco);
    }

    languageThemeSetup(monaco);
    disposablesRef.current.push(
      tokenizerSetup(monaco, fields, globalParameters, keywords, constants),
      completionItemProviderSetup(monaco, fields, globalParameters)
    );
  };

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    if (editorRef.current) {
      editorRef.current.setValue(internalExpression ?? "");
    }
    setEditor(editor);
    validateExpression();
  };

  const validateExpression = () => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    const text = editor.getValue();

    // Lists of valid identifiers
    let mathjsFunctions = Object.keys(math);
    let jsFuncs = jsFunctions.map((f) => f.label);
    const fieldNames = fields.map((f) => f.expressionText || f);
    const globalParams = globalParameters.map((p) => p.name);

    // Create markers array
    const markers: editor.IMarkerData[] = [];

    // Find all string literals and their positions
    const stringRanges: StringRange[] = [];
    const stringRegex = /"([^"\\]|\\.)*"|'([^'\\]|\\.)*'/g;
    let stringMatch;

    while ((stringMatch = stringRegex.exec(text)) !== null) {
      stringRanges.push({
        start: stringMatch.index,
        end: stringMatch.index + stringMatch[0].length,
      });
    }

    // Helper function to check if a position is inside any string
    const isInsideString = (position) => {
      return stringRanges.some((range) => position >= range.start && position < range.end);
    };

    // Find all identifiers but exclude those in strings
    const identifierRegex = /\b[a-zA-Z_]\w*\b/g;
    let match;

    while ((match = identifierRegex.exec(text)) !== null) {
      const word = match[0];
      const startPos = match.index;

      // Skip if this position is inside a string
      if (isInsideString(startPos)) {
        continue;
      }

      // Skip if it's a known word
      if (
        mathjsFunctions.includes(word) ||
        fieldNames.includes(word) ||
        keywords.includes(word) ||
        globalParams.includes(word) ||
        jsFuncs.includes(word)
      ) {
        continue;
      }

      // Calculate position
      const textUntilMatch = text.substring(0, startPos);
      const lines = textUntilMatch.split("\n");
      const lineNumber = lines.length;
      const column = lines[lines.length - 1].length + 1;

      markers.push({
        severity: monaco.MarkerSeverity.Error,
        message: `Unknown symbol: '${word}'`,
        startLineNumber: lineNumber,
        startColumn: column,
        endLineNumber: lineNumber,
        endColumn: column + word.length,
      });
    }

    // Set markers
    monaco.editor.setModelMarkers(model, "unknown-symbols", markers);
  };

  return (
    <MonacoEditor
      height={200}
      language={"Atlas"}
      theme="atlasTheme"
      beforeMount={handleEditorWillMount}
      onMount={handleEditorDidMount}
      onChange={(value) => setInternalExpression(value)}
      options={{
        minimap: { enabled: false },
        colorDecorators: true,
        lineNumbers: "off",
        overviewRulerBorder: false,
        stickyScroll: { enabled: false },
        fixedOverflowWidgets: true,
      }}
    />
  );
};

//#region Helper Functions
function languageSetup(monaco) {
  monaco.languages.register({ id: "Atlas" });
  monaco.languages.setLanguageConfiguration("Atlas", {
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });
}

function languageThemeSetup(monaco) {
  monaco.editor.defineTheme("atlasTheme", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "mathjs-function", foreground: "#910000", fontStyle: "bold" },
      { token: "javascript-function", foreground: "#FF00FF", fontStyle: "bold" },
      { token: "fields", foreground: "#8B0089", fontStyle: "italic" },
      { token: "global-parameter", foreground: "#0C7F07", fontStyle: "italic" },
      { token: "keyword", foreground: "#0000FF", fontStyle: "bold" },
      { token: "number", foreground: "#D37735" },
      { token: "string", foreground: "#A31515" },
      { token: "comment", foreground: "#008000", fontStyle: "italic" },
      { token: "constant", foreground: "#8565FF", fontStyle: "bold" },
    ],
    colors: {
      "editor.background": "#f5f5f5", // Light gray background
    },
  });
}

function completionItemProviderSetup(monaco, fields, globalParameters) {
  const disposable = monaco.languages.registerCompletionItemProvider("Atlas", {
    provideCompletionItems: (model, position) => {
      const suggestions = [
        {
          label: "equalText",
          kind: monaco.languages.CompletionItemKind.Function,
          documentation: "Compares two text values for equality",
          detail: "equalText(text1, text2)",
          insertText: "equalText( , )",
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
      ];

      Object.keys(math).forEach((funcName) => {
        if (typeof math[funcName] === "function") {
          suggestions.push({
            label: funcName,
            kind: monaco.languages.CompletionItemKind.Function,
            documentation: `MathJS function: ${funcName}`,
            detail: `${funcName}()`,
            insertText: `${funcName}(\${1})`,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          });
        }
      });

      jsFunctions.forEach(({ requiresPrefix, insertText, ...jsFunc }) => {
        if (requiresPrefix) {
          const wordInfo = model.getWordUntilPosition(position);
          const textBeforeWord = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: wordInfo.startColumn,
          });
          if (!textBeforeWord.trimEnd().endsWith(requiresPrefix)) {
            insertText = requiresPrefix + insertText;
          }
        }
        suggestions.push({
          ...jsFunc,
          insertText,
          kind: monaco.languages.CompletionItemKind.Function,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        });
      });

      fields.forEach((field) => {
        const fieldName = field.expressionText || field;
        suggestions.push({
          label: fieldName,
          kind: monaco.languages.CompletionItemKind.Field,
          documentation: `Field: ${fieldName}`,
          detail: fieldName,
          insertText: fieldName,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        });
      });

      globalParameters.forEach((param) => {
        suggestions.push({
          label: param.name,
          kind: monaco.languages.CompletionItemKind.Variable,
          documentation: `Global Parameter Value: ${param.value}`,
          detail: param.name,
          insertText: param.name,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        });
      });

      constants.forEach((constant: Constant) => {
        suggestions.push({
          label: constant.name,
          kind: monaco.languages.CompletionItemKind.Constant,
          documentation: `Constant Value: ${constant.value}`,
          detail: `${constant.aliases.join(", ")}`,
          insertText: constant.name,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        });
      });

      return { suggestions };
    },
    triggerCharacters: [".", "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"],
  });
  return disposable;
}

function tokenizerSetup(
  monaco,
  fields,
  globalParameters,
  keywords: string[],
  constants: Constant[]
) {
  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const createSafeRegex = (items: string[], suffix = "") => {
    const safeItems = items
      .filter((item) => item?.toString()?.trim())
      .map((item) => escapeRegex(item.toString()));

    if (safeItems.length === 0) return null;
    return new RegExp(`\\b(${safeItems.join("|")})\\b${suffix}`, "g");
  };

  const mathjsRegex = createSafeRegex(Object.keys(math), "(?=\\s*\\()");
  const fieldNames = fields
    .map((field: { expressionText: string }) => field.expressionText || field)
    .filter((name: string) => name);
  const fieldsRegex = createSafeRegex(fieldNames);
  const globalParamsRegex = createSafeRegex(
    globalParameters.map((param: { name: string }) => param.name)
  );
  const keywordsRegex = createSafeRegex(keywords);
  const constantNames = constants.flatMap((constant) => constant.aliases).filter((alias) => alias);
  const constantsRegex = createSafeRegex(constantNames);
  const jsFunctionsRegex = createSafeRegex(
    jsFunctions.map((f) => f.label),
    "(?=\\s*\\()"
  );

  const rules = [
    [/\b\d+(\.\d+)?\b/, "number"],
    [/"([^"\\]|\\.)*"/, "string"],
    [/'([^'\\]|\\.)*'/, "string"],
    [/#.*$/, "comment"],
    [/[a-zA-Z_]\w*/, "identifier"],
  ];

  if (keywordsRegex) rules.unshift([keywordsRegex, "keyword"]);
  if (fieldsRegex) rules.unshift([fieldsRegex, "fields"]);
  if (mathjsRegex) rules.unshift([mathjsRegex, "mathjs-function"]);
  if (globalParamsRegex) rules.unshift([globalParamsRegex, "global-parameter"]);
  if (constantsRegex) rules.unshift([constantsRegex, "constant"]);
  if (jsFunctionsRegex) rules.unshift([jsFunctionsRegex, "javascript-function"]);

  const disposable = monaco.languages.setMonarchTokensProvider("Atlas", {
    tokenizer: {
      root: rules,
    },
  });

  return disposable;
}

//#endregion Helper Functions
